// La fiche d'un employeur en Radar (lot 3 de la refonte, maquette « Fiche
// employeur »). Elle remplace « Toutes les offres » à la bonne échelle : un
// employeur, ses offres dans vos métiers avec leur score, et le reste replié
// avec une recherche, pour que rien n'échappe sans tableau de 60 000 px.
//
// POURQUOI ELLE EXISTE. L'accueil disait « Elia : 10 offres dans vos métiers,
// la meilleure à 64 » et n'en montrait qu'une : le fil ne garde que ce qui
// passe le seuil. Les autres n'étaient plus nulle part depuis le lot 2.

import { useState } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff, Search } from 'lucide-react'
import { get, post } from '../lib/api'
import { useT } from '../lib/i18n'
import { fullDate, relTime } from '../lib/format'
import { navigate } from '../lib/router'
import { useMots } from '../lib/vocabulaire'
import { useData, Loading, Empty, ActionButton, useToast } from '../components/common'
import { LogoEntreprise } from '../components/sites'
import { useEtatEmployeur } from '../components/employeurs'
import { useSuivreSite } from '../components/filtreSite'
import { signalerTache } from '../components/bandeauTaches'
import { Carte, type CarteOffre } from './Accueil'

interface Fiche {
  id: number
  nom: string
  url: string
  statut: 'prioritaire' | 'muet' | 'normal'
  logo: string | null
  annonces: boolean
  suivi: boolean
  suiviDepuis: string | null
  lu: string | null
  enErreur: boolean
  erreurDepuis: string | null
  seuil: number
  dansMetiers: (CarteOffre & { etat: string | null })[]
  autres: { id: number; titre: string; lieu: string | null; employeur: string | null; vueLe: string }[]
  autresTotal: number
}

export function FicheEmployeur({ id, onChanged }: { id: number; onChanged: () => void }) {
  const t = useT()
  const mots = useMots()
  const toast = useToast()
  const { suivre, fenetre } = useSuivreSite()
  const etatDe = useEtatEmployeur()
  const fiche = useData(() => get<Fiche>(`/api/sources/${id}/fiche`), [id])
  const [autresOuvert, setAutresOuvert] = useState(false)
  const [q, setQ] = useState('')

  if (fiche.loading) return <Loading label={t('common.loading')} />
  if (fiche.error || !fiche.data) return <Empty>{fiche.error ?? t('emp.introuvable')}</Empty>
  const f = fiche.data
  const meilleure = f.dansMetiers.length ? f.dansMetiers[0].score : null
  const etat = etatDe({
    id: f.id,
    nom: f.nom,
    logo: f.logo,
    annonces: f.annonces,
    dansMetiers: f.dansMetiers.length,
    meilleure,
    auDessus: f.dansMetiers.filter((o) => o.score >= f.seuil).length,
    seuil: f.seuil,
    derniere: f.dansMetiers.reduce<string | null>((d, o) => (!d || o.vueLe > d ? o.vueLe : d), null),
    enErreur: f.enErreur,
    erreurDepuis: f.erreurDepuis,
    lu: f.lu,
  })
  const etiquette = (e: string | null) =>
    e === 'candidature' ? t('emp.etatCandidature') : e === 'later' ? mots('etat.later') : e === 'skip' ? mots('etat.skip') : undefined
  const filtre = q.trim().toLowerCase()
  const autres = filtre
    ? f.autres.filter((o) => `${o.titre} ${o.lieu ?? ''} ${o.employeur ?? ''}`.toLowerCase().includes(filtre))
    : f.autres

  return (
    <div className="body acc">
      {fenetre}
      <button className="linkbtn emp-retour" onClick={() => navigate('#/sources')}>
        <ChevronLeft size={15} />{t('emp.retour')}
      </button>

      <header className="emp-tete">
        <LogoEntreprise nom={f.nom} logo={f.logo} className="pc-logo emp-logo" />
        <div className="grow">
          <h1>{f.nom}</h1>
          <span className="acc-gris emp-meta">
            {[
              f.url.replace(/^https?:\/\//, '').replace(/\/$/, ''),
              f.lu ? t('emp.luQuand', { quand: relTime(f.lu) }) : null,
              f.suiviDepuis ? t('emp.suiviDepuis', { date: fullDate(f.suiviDepuis) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <ActionButton
          size="sm"
          variant={f.suivi ? 'secondary' : 'primary'}
          onClick={async () => {
            if (f.suivi) await post(`/api/sources/${f.id}/abonnement`, { abonne: false })
            else {
              // Un gros site ouvre la fenêtre de filtre (issue #191).
              if (!(await suivre(f.id, f.nom))) return
              signalerTache()
            }
            toast(f.suivi ? t('sources.unfollowed', { name: f.nom }) : t('sources.followed', { name: f.nom }))
            fiche.reload()
            onChanged()
          }}
        >
          {f.suivi ? <><EyeOff size={13} />{t('sources.unfollow')}</> : <><Eye size={13} />{t('sources.follow')}</>}
        </ActionButton>
      </header>

      <p className={`emp-phrase${etat.ton ? ` ${etat.ton}` : ''}`}>{etat.texte}</p>

      {/* Prioritaire ou muet, pour les mails : un employeur, pas un site d'annonces. */}
      {f.suivi && !f.annonces && (
        <div className="regions">
          {(['prioritaire', 'muet'] as const).map((st) => {
            const on = f.statut === st
            return (
              <button
                key={st}
                type="button"
                aria-pressed={on}
                className={`region${on ? ' on' : ''}`}
                title={t(st === 'prioritaire' ? 'mails.prioritaireAide' : 'mails.muetAide')}
                onClick={async () => {
                  await post('/api/radar/rythme', { mails: { employeur: f.id, statut: on ? 'normal' : st } })
                  toast(on ? t('mails.saved') : t(st === 'prioritaire' ? 'mails.prioritaireAide' : 'mails.muetAide'))
                  fiche.reload()
                  onChanged()
                }}
              >
                {on && <Check size={12} strokeWidth={3} />}
                {t(st === 'prioritaire' ? 'mails.prioritaire' : 'mails.muet')}
              </button>
            )
          })}
        </div>
      )}

      {f.dansMetiers.length > 0 && (
        <section className="acc-sec">
          <div className="acc-eyebrow">{t('emp.dansMetiersTitre')}</div>
          <div className="acc-cartes">
            {f.dansMetiers.map((o) => <Carte key={o.id} o={o} sous={etiquette(o.etat)} />)}
          </div>
        </section>
      )}

      {f.autresTotal > 0 && (
        <section className="acc-sec">
          <button className="acc-repli" aria-expanded={autresOuvert} onClick={() => setAutresOuvert((v) => !v)}>
            <b>
              {f.autresTotal === 1 ? t('emp.autresUn', { nom: f.nom }) : t('emp.autresPlus', { n: f.autresTotal, nom: f.nom })}
            </b>
            {autresOuvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {autresOuvert && (
            <div className="acc-liste">
              <label className="acc-ligne emp-cherche">
                <Search size={15} />
                <input
                  className="grow"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t('emp.chercher')}
                  aria-label={t('emp.chercher')}
                />
              </label>
              {autres.map((o) => (
                <a className="acc-ligne acc-lien" key={o.id} href={`#/offre/${o.id}`}>
                  <span className="grow">
                    <b className="emp-autre-t">{o.titre}</b>
                    <span className="acc-etat calme">{[f.annonces ? o.employeur : null, o.lieu].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="emp-evaluer">{t('emp.evaluer')}</span>
                </a>
              ))}
              {autres.length === 0 && <div className="acc-ligne acc-gris">{t('emp.aucunResultat')}</div>}
            </div>
          )}
          <p className="field-help">{t('emp.autresAide')}</p>
        </section>
      )}

      {f.dansMetiers.length === 0 && f.autresTotal === 0 && <Empty>{t('emp.vide')}</Empty>}
    </div>
  )
}
